/**
 * Tags Field Type
 *
 * Provides configuration and renderers for displaying array values as individual tags.
 * This is a read-only field type for displaying arrays as tag chips with boundaries.
 *
 * Automatically handles arrays of:
 * - Strings: ['a', 'b', 'c'] → [a] [b] [c]
 * - Objects with `name`: [{name: 'a'}, {name: 'b'}] → [a] [b]
 * - Objects with `label`: [{label: 'a'}, {label: 'b'}] → [a] [b]
 *
 * Usage:
 * ```tsx
 * categories: {
 *   id: 'categories',
 *   label: t`Categories`,
 *   type: 'tags',
 * }
 * ```
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { TextInput } from 'components';
import { CellContainer } from '../../styles';
import { TagsWithOverflow } from '../../components/TagsWithOverflow';
import type {
  FieldTypeConfig,
  FormFieldProps,
  ColumnRenderer,
  RenderOptions,
  CustomColumn,
} from '../../types';
import { L } from './columnWidths';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert an array value to an array of strings for tag display
 * Handles arrays of strings, objects with `name`, or objects with `label`
 */
const parseTagsValue = (value: unknown): string[] => {
  if (!value) return [];

  // If already a string, split by comma (for backward compatibility)
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }

  // If not an array, convert to single-item array
  if (!Array.isArray(value)) return [String(value)];

  // Empty array
  if (value.length === 0) return [];

  // Map array items to strings
  return value
    .map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        // Try `name` first, then `label`
        return (
          (item as { name?: string; label?: string }).name ||
          (item as { name?: string; label?: string }).label ||
          ''
        );
      }
      return String(item ?? '');
    })
    .filter(Boolean);
};

/**
 * Format tags as comma-separated string (for tooltip display)
 */
const formatTagsTooltip = (value: unknown): string => {
  return parseTagsValue(value).join(', ');
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Tags read-only renderer - displays values as individual tag chips
 * Automatically formats arrays into tag elements with boundaries
 * Includes internal Tooltip for consistent hover behavior
 */
export const tagsReadOnlyRenderer = <T extends { id: number | string }>(
  _col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    const value = cellContext.getValue();
    const tags = parseTagsValue(value);

    if (tags.length === 0) {
      return <CellContainer />;
    }

    return (
      <CellContainer>
        <TagsWithOverflow
          values={tags.map((tag, idx) => ({ id: `${tag}-${idx}`, label: tag }))}
          columnWidth={cellContext.column.getSize()}
        />
      </CellContainer>
    );
  },
});

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Tags field type configuration
 * Read-only display for tag values
 */
export const tagsFieldType: FieldTypeConfig = {
  placeholder: '',
  ...L,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const tagsReadOnly: ColumnRenderer = tagsReadOnlyRenderer;

/** Editable renderer - tags type is read-only, falls back to read-only */
export const tagsEditable: ColumnRenderer = tagsReadOnlyRenderer;

// ============================================================================
// Form Field Renderer
// ============================================================================

/**
 * Render a tags field for forms (e.g. bulk edit).
 * Displays/edits as comma-separated text; value is stored as string, converted to array on submit via fieldTransform.
 */
export const renderTagsField = ({
  col,
  value,
  onChange,
  disabled,
  isFirst,
  inputRef,
  onEnterPress,
}: FormFieldProps): React.ReactNode => {
  const emptyDisplayNode = (col as { emptyDisplayNode?: React.ReactNode }).emptyDisplayNode;
  const displayValue = typeof value === 'string' ? value : parseTagsValue(value).join(', ');
  const valueEmpty = value == null || value === '' || (Array.isArray(value) && value.length === 0);
  const showEmptyChip = valueEmpty && emptyDisplayNode;

  return (
    <TextInput
      key={col.key}
      id={col.key}
      label={col.label}
      placeholder={showEmptyChip ? '' : col.placeholder ?? 'Enter values separated by commas'}
      fullWidth
      disabled={disabled}
      ref={isFirst ? inputRef : undefined}
      autoFocus={isFirst}
      value={displayValue}
      onChange={e => onChange(col.key, e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter' && onEnterPress) {
          e.preventDefault();
          e.stopPropagation();
          onEnterPress();
        }
      }}
      labelSeparate
      variant="outlined"
      InputProps={showEmptyChip ? { startAdornment: emptyDisplayNode } : undefined}
    />
  );
};
